import { Entity } from '@adornis/baseql/decorators.js';
import type { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { nothing, type TemplateResult } from 'lit';
import { ProductWaitinglistPriorityRule } from '../../entities/ProductWaitinglistPriorityRule.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { BHTranslationDictionary } from '../../translations/translation.js';
import type { RegistrationProductWaitinglistPriorityRulesPlugin } from '../plugin-types.js';
import type { IcsrmStammdaten } from '../stammdaten/icsrm-stammdaten-plugin.js';

@Entity()
export class IcsrmImmigrationWaitinglistPriorityRule extends ProductWaitinglistPriorityRule {
  static override _class = 'IcsrmImmigrationWaitinglistPriorityRule';

  override hasValue(): boolean {
    return true;
  }

  override renderEditUI(opt: { tr: TranslationController<BHTranslationDictionary> }) {
    return nothing as unknown as TemplateResult<1>;
  }

  override async hasHighPriority(userID: string): Promise<boolean> {
    const userStammdaten = await UserStammdaten.getByUserAndInstitution<IcsrmStammdaten>(
      userID,
      this.institutionID,
    )({ isImmigrant: 1, nationality: 1 });

    if (!userStammdaten)
      throw new Error('could not find stammdaten for user=' + userID + ', institution=' + this.institutionID);

    return !!userStammdaten.isImmigrant || userStammdaten.nationality !== 'DE';
  }

  override async label() {
    return [
      'Hohe Priorität wenn eine Zuwanderungsgeschichte vorhanden ist',
      'High priority when immigration history is present',
    ] satisfies [string, string];
  }
}

@Entity()
export class IcsrmUserIsBornInCountryWaitinglistPriorityRule extends ProductWaitinglistPriorityRule {
  static override _class = 'IcsrmUserIsBornInCountryWaitinglistPriorityRule';

  override hasValue(): boolean {
    return true;
  }

  override renderEditUI(opt: { tr: TranslationController<BHTranslationDictionary> }) {
    return nothing as unknown as TemplateResult<1>;
  }

  override async hasHighPriority(userID: string): Promise<boolean> {
    const userStammdaten = await UserStammdaten.getByUserAndInstitution<IcsrmStammdaten>(
      userID,
      this.institutionID,
    )({ nationality: 1 });

    if (!userStammdaten)
      throw new Error('could not find stammdaten for user=' + userID + ', institution=' + this.institutionID);

    return userStammdaten.nationality !== 'DE';
  }

  override async label() {
    return [
      'Hohe Priorität wenn die Nationalität nicht Deutsch ist',
      'High priority when nationality is not German',
    ] satisfies [string, string];
  }
}

export const icsrmRegistrationProductWaitinglistPriorityRulesPlugin: RegistrationProductWaitinglistPriorityRulesPlugin =
  {
    id: 'registration::product-waitinglist-priority-rules::icsrm',
    type: 'registration::product-waitinglist-priority-rules',
    getRuleKinds() {
      return [IcsrmImmigrationWaitinglistPriorityRule, IcsrmUserIsBornInCountryWaitinglistPriorityRule];
    },
    blockTimeForLowPriorityInDays: 0,
  };
